"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Asterisk } from "./Asterisk";
import { Logo } from "./Logo";

const navLinks = [
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/thinking", label: "Thinking" },
  { href: "/posts", label: "Posts" },
  { href: "/contact", label: "Contact" },
];

export default function Navigation() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (pathname?.startsWith("/admin")) return null;

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname?.startsWith(href + "/");

  return (
    <>
      <nav
        className="fixed inset-x-0 top-0 z-50 grid items-center gap-4 px-page transition-[padding,border-color,background] duration-[400ms] lg:gap-8"
        style={{
          gridTemplateColumns: "auto 1fr auto auto",
          paddingTop: scrolled ? 12 : 18,
          paddingBottom: scrolled ? 12 : 18,
          background: "color-mix(in srgb, var(--warm) 92%, transparent)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: scrolled ? "1px solid var(--rule)" : "1px solid transparent",
        }}
      >
        {/* Logo lockup */}
        <Link
          href="/"
          className="text-olive"
          onClick={() => setOpen(false)}
          aria-label="Evoke Advisory home"
        >
          <Logo lockup="full" scale={0.82} color="var(--olive)" />
        </Link>

        {/* Desktop nav */}
        <ul className="m-0 hidden list-none justify-center gap-8 p-0 lg:flex">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="relative inline-flex min-h-[44px] items-center gap-2 py-2.5 text-[14px] tracking-wide text-olive transition-colors"
                  style={{ fontWeight: active ? 600 : 500 }}
                >
                  {link.label}
                  <span
                    aria-hidden
                    className="absolute bottom-1 left-1/2 h-px bg-olive transition-[width,left] duration-[400ms]"
                    style={{
                      width: active ? "100%" : 0,
                      left: active ? 0 : "50%",
                    }}
                  />
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Desktop CTA */}
        <Link
          href="/contact"
          className="hidden items-center gap-2.5 border border-olive px-4 py-3 text-[13px] font-semibold tracking-wide text-olive transition-colors hover:bg-olive hover:text-lime lg:inline-flex"
          style={{ background: "var(--lime)" }}
          onClick={() => setOpen(false)}
        >
          <Asterisk size={12} color="var(--olive)" />
          Start a conversation
        </Link>

        {/* Mobile hamburger — 44×44 hit target, SVG icon, no dropdown */}
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-olive lg:hidden"
          style={{ gridColumn: 4 }}
        >
          <svg width="22" height="14" viewBox="0 0 22 14" aria-hidden="true">
            <path d="M0 1h22M0 7h22M0 13h22" stroke="currentColor" strokeWidth="1.4" />
          </svg>
        </button>
      </nav>

      {/* Mobile drawer — full-screen overlay on phone only */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex flex-col lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={{ background: "var(--olive)", color: "var(--warm)" }}
        >
          <div className="flex items-center justify-between px-page py-5">
            <Link href="/" onClick={() => setOpen(false)} aria-label="Evoke Advisory home">
              <Logo lockup="full" color="var(--lime)" style={{ height: 28 }} />
            </Link>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-warm"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M2 2l16 16M18 2L2 18" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
          </div>

          <nav className="flex flex-col gap-2 px-page pt-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="border-b py-3 font-serif italic text-warm"
                style={{
                  fontSize: 36,
                  lineHeight: 1.1,
                  borderColor: "var(--rule-on-olive)",
                  fontWeight: isActive(link.href) ? 500 : 400,
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mt-auto flex items-end justify-between px-page pb-8">
            <a
              href="mailto:hello@evokeadvisory.au"
              onClick={() => setOpen(false)}
              className="lowercase text-[13px] uppercase tracking-[0.14em]"
              style={{ color: "color-mix(in srgb, var(--warm) 80%, transparent)" }}
            >
              hello@evokeadvisory.au
            </a>
            <Asterisk size={56} color="var(--lime)" pinkCenter />
          </div>
        </div>
      )}
    </>
  );
}
